import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import {
  Send, Users, X, CheckCircle, Paperclip, ChevronUp, ChevronDown,
  Plus, Minus, Type, AlignLeft, MousePointer, Minus as DividerIcon,
  Braces, Upload, Eye, Pencil, AlertTriangle, BookmarkPlus,
} from 'lucide-react';

// ─── Helpers ───────────────────────────────────────────────────────────────

const uid = () => Math.random().toString(36).slice(2, 9);

const escHtml = (s = '') =>
  String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

// Render **bold** markers → <strong> tags, HTML-escaping everything else
const renderBold = (text = '') => {
  const parts = String(text).split(/\*\*(.*?)\*\*/g);
  return parts.map((part, i) =>
    i % 2 === 0 ? escHtml(part) : `<strong>${escHtml(part)}</strong>`
  ).join('');
};

const blockToHtml = (block) => {
  switch (block.type) {
    case 'heading':
      return `<h${block.level || 2} style="color:#0F172A;font-family:Georgia,serif;font-weight:bold;margin:0 0 16px 0;font-size:${block.level === 1 ? '24' : '20'}px;line-height:1.3;">${escHtml(block.text)}</h${block.level || 2}>`;
    case 'paragraph':
      return `<p style="color:#374151;font-size:15px;line-height:1.8;margin:0 0 16px 0;">${renderBold(block.text).replace(/\n/g, '<br>')}</p>`;
    case 'button':
      return `<div style="text-align:center;margin:28px 0;"><a href="${escHtml(block.url)}" style="display:inline-block;background:#B11F24;color:#ffffff;text-decoration:none;padding:13px 36px;border-radius:6px;font-size:15px;font-weight:bold;font-family:Georgia,serif;">${escHtml(block.label)}</a></div>`;
    case 'divider':
      return `<hr style="border:none;border-top:1px solid #e5e7eb;margin:24px 0;">`;
    default:
      return '';
  }
};

const blocksToHtml = (blocks) => blocks.map(blockToHtml).join('\n');

const applyPh = (str, data) =>
  str.replace(/\{\{(\w+)\}\}/g, (_, k) => (data && data[k] != null ? data[k] : `{{${k}}}`));

const wrapEmailPreview = (innerHtml, logoUrl) => {
  const logo = logoUrl
    ? `<img src="${escHtml(logoUrl)}" alt="FB Attorneys Academy" height="55" style="display:block;margin:0 auto;max-width:200px;">`
    : '';
  return `<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1">
</head><body style="margin:0;padding:0;background:#f4f4f4;font-family:Georgia,serif;">
<table width="100%" cellpadding="0" cellspacing="0" style="background:#f4f4f4;padding:24px 0;"><tr><td align="center">
<table width="600" cellpadding="0" cellspacing="0" style="background:#ffffff;border-radius:8px;overflow:hidden;box-shadow:0 2px 8px rgba(0,0,0,0.08);">
<tr><td style="background:#1C2B2B;padding:24px 40px;text-align:center;">${logo}</td></tr>
<tr><td style="padding:40px;">${innerHtml}</td></tr>
<tr><td style="background:#1C2B2B;padding:24px 40px;text-align:center;">
  <p style="margin:0 0 4px;color:#ffffff;font-size:14px;font-weight:bold;font-family:Arial,sans-serif;">FB Attorneys Academy</p>
  <p style="margin:0 0 10px;color:#0D9488;font-size:12px;font-family:Arial,sans-serif;">Legal &amp; Tech Hub</p>
  <p style="margin:0;color:#9ca3af;font-size:12px;font-family:Arial,sans-serif;">8th Floor, Amani Place, Ohio Street, Dar es Salaam</p>
</td></tr>
</table></td></tr></table>
</body></html>`;
};

// Build full template HTML matching the existing seeded template format.
// Uses {{logo_url}} placeholder so the logo stays dynamic at send time.
const buildTemplateHtml = (innerHtml) => {
  const HDR = '<table width="100%" cellpadding="0" cellspacing="0" style="background:#1C2B2B;"><tr><td style="padding:24px 40px;text-align:center;"><img src="{{logo_url}}" alt="FB Attorneys Academy" height="55" style="display:block;margin:0 auto;max-width:200px;" /></td></tr></table>';
  const NOTE = '<div style="background:#f0fdfa;border-left:4px solid #0D9488;padding:14px 18px;border-radius:4px;margin:24px 0 0;"><p style="margin:0;color:#1C2B2B;font-size:13px;font-family:Arial,sans-serif;">Please note: This is an automated message from a no-reply email address. Kindly do not respond to this email.</p></div>';
  const FTR = '<table width="100%" cellpadding="0" cellspacing="0" style="background:#1C2B2B;"><tr><td style="padding:24px 40px;text-align:center;"><p style="margin:0 0 4px;color:#ffffff;font-size:14px;font-weight:bold;font-family:Arial,sans-serif;">FB Attorneys Academy</p><p style="margin:0 0 10px;color:#0D9488;font-size:12px;font-family:Arial,sans-serif;">Legal &amp; Tech Hub</p><p style="margin:0;color:#9ca3af;font-size:12px;font-family:Arial,sans-serif;">8th Floor, Amani Place, Ohio Street, Dar es Salaam</p></td></tr></table>';
  return '<!DOCTYPE html><html><head><meta charset="UTF-8"><meta name="viewport" content="width=device-width,initial-scale=1.0"></head>'
    + '<body style="margin:0;padding:0;background:#f4f4f4;font-family:Georgia,serif;">'
    + '<table width="100%" cellpadding="0" cellspacing="0" style="background:#f4f4f4;padding:32px 0;"><tr><td align="center">'
    + '<table width="600" cellpadding="0" cellspacing="0" style="background:#ffffff;border-radius:8px;overflow:hidden;box-shadow:0 2px 8px rgba(0,0,0,0.08);">'
    + HDR + '<tr><td style="padding:40px;">' + innerHtml + NOTE + '</td></tr>' + FTR
    + '</table></td></tr></table></body></html>';
};

const toTemplateKey = (name) =>
  'custom_' + name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '') + '_' + Math.random().toString(36).slice(2, 6);

const extractPlaceholders = (subject, bodyHtml) => {
  const matches = (subject + ' ' + bodyHtml).match(/\{\{(\w+)\}\}/g) || [];
  return [...new Set(matches.map(m => m.slice(2, -2)))];
};

const parseCSV = (text) => {
  const lines = text.trim().split(/\r?\n/);
  if (lines.length < 2) return { columns: [], rows: [], error: 'CSV has no data rows' };
  const headers = lines[0].split(',').map(h => h.trim().replace(/^["']|["']$/g, ''));
  const emailIdx = headers.findIndex(h => h.toLowerCase() === 'email');
  if (emailIdx === -1) return { columns: headers, rows: [], error: 'No "email" column found in CSV' };
  const nonEmailCols = headers.filter((_, i) => i !== emailIdx);
  const rows = lines.slice(1)
    .map(line => {
      const vals = line.split(',').map(v => v.trim().replace(/^["']|["']$/g, ''));
      const email = vals[emailIdx] || '';
      const data = {};
      headers.forEach((h, i) => { if (i !== emailIdx) data[h] = vals[i] || ''; });
      return { email, data };
    })
    .filter(r => r.email.includes('@'));
  return { columns: nonEmailCols, rows, error: null };
};

const fmt = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// ─── Standard placeholders always available for modes 1 & 2 ────────────────

const STANDARD_PH = [
  { key: 'applicant_name', label: 'Applicant / Student Name' },
  { key: 'student_name',   label: 'Student Name' },
  { key: 'email',          label: 'Recipient Email' },
  { key: 'intake_name',    label: 'Intake Period Name' },
  { key: 'cohort_name',    label: 'Cohort Name' },
  { key: 'cohort_start_date', label: 'Cohort Start Date' },
];

// ─── Chip ──────────────────────────────────────────────────────────────────

function Chip({ chip, onRemove, removable = true }) {
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium max-w-[220px]
        ${chip.isExternal
          ? 'bg-amber-50 text-amber-800 border border-amber-300'
          : 'bg-primary/10 text-primary border border-primary/20'}`}
    >
      {chip.isExternal && <AlertTriangle className="w-3 h-3 flex-shrink-0 text-amber-500" />}
      <span className="truncate">{chip.label || chip.email}</span>
      {removable && (
        <button
          onClick={() => onRemove(chip.email)}
          className="ml-0.5 hover:opacity-70 flex-shrink-0"
          type="button"
        >
          <X className="w-3 h-3" />
        </button>
      )}
    </span>
  );
}

// ─── Block editor ─────────────────────────────────────────────────────────

function BlockEditor({ block, index, total, onUpdate, onRemove, onMove, onTrackFocus }) {
  const trackFocus = (fieldName) => (e) => onTrackFocus({ field: 'block', blockId: block.id, fieldName, el: e.target });

  const shared = 'w-full border border-gray-200 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30';

  return (
    <div className="group relative border border-gray-200 rounded-lg bg-white">
      {/* Block header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100 bg-gray-50 rounded-t-lg">
        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
          {block.type === 'heading' ? `H${block.level || 2} Heading`
           : block.type === 'paragraph' ? 'Paragraph'
           : block.type === 'button' ? 'Button / CTA'
           : 'Divider'}
        </span>
        <div className="flex items-center gap-1">
          <button onClick={() => onMove(index, -1)} disabled={index === 0}
            className="p-1 rounded text-gray-400 hover:text-gray-700 disabled:opacity-30" title="Move up">
            <ChevronUp className="w-3.5 h-3.5" />
          </button>
          <button onClick={() => onMove(index, 1)} disabled={index === total - 1}
            className="p-1 rounded text-gray-400 hover:text-gray-700 disabled:opacity-30" title="Move down">
            <ChevronDown className="w-3.5 h-3.5" />
          </button>
          <button onClick={() => onRemove(block.id)}
            className="p-1 rounded text-gray-400 hover:text-red-500" title="Remove block">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Block fields */}
      <div className="p-3 space-y-2">
        {block.type === 'heading' && (
          <div className="flex gap-2">
            <select
              value={block.level || 2}
              onChange={e => onUpdate(block.id, 'level', Number(e.target.value))}
              className="border border-gray-200 rounded px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 w-20"
            >
              <option value={1}>H1</option>
              <option value={2}>H2</option>
            </select>
            <input
              className={`${shared} flex-1`}
              placeholder="Heading text…"
              value={block.text || ''}
              onChange={e => onUpdate(block.id, 'text', e.target.value)}
              onFocus={trackFocus('text')}
              onMouseUp={trackFocus('text')}
              onKeyUp={trackFocus('text')}
            />
          </div>
        )}
        {block.type === 'paragraph' && (
          <textarea
            className={`${shared} resize-none`}
            rows={4}
            placeholder="Paragraph text… Use {{applicant_name}} for personalisation. Wrap text in **double asterisks** for bold."
            value={block.text || ''}
            onChange={e => onUpdate(block.id, 'text', e.target.value)}
            onFocus={trackFocus('text')}
            onMouseUp={trackFocus('text')}
            onKeyUp={trackFocus('text')}
          />
        )}
        {block.type === 'button' && (
          <div className="flex gap-2">
            <input
              className={`${shared} w-36`}
              placeholder="Button label"
              value={block.label || ''}
              onChange={e => onUpdate(block.id, 'label', e.target.value)}
              onFocus={trackFocus('label')}
              onMouseUp={trackFocus('label')}
              onKeyUp={trackFocus('label')}
            />
            <input
              className={`${shared} flex-1`}
              placeholder="https://…"
              value={block.url || ''}
              onChange={e => onUpdate(block.id, 'url', e.target.value)}
              onFocus={trackFocus('url')}
              onMouseUp={trackFocus('url')}
              onKeyUp={trackFocus('url')}
            />
          </div>
        )}
        {block.type === 'divider' && (
          <hr className="border-gray-200 my-1" />
        )}
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────

export default function AdminBulkEmailPage() {
  const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  // ── Data ──
  const [cohorts, setCohorts] = useState([]);
  const [allEmails, setAllEmails] = useState([]); // [{email, label, type}]
  const [logoUrl, setLogoUrl] = useState('');

  // ── Recipient mode ──
  const [mode, setMode] = useState('individual'); // 'individual' | 'cohort' | 'csv'
  const [chips, setChips] = useState([]);        // {email, label, isExternal, data?}
  const [search, setSearch] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [selectedCohortId, setSelectedCohortId] = useState('');
  const [csvColumns, setCsvColumns] = useState([]); // non-email column names from CSV
  const [csvError, setCsvError] = useState('');
  const searchRef = useRef(null);

  // ── Compose ──
  const [subject, setSubject] = useState('');
  const [blocks, setBlocks] = useState([{ id: uid(), type: 'paragraph', text: '' }]);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null); // {template_key, name, subject, body_html}

  // ── Attachments ──
  const [attachments, setAttachments] = useState([]);
  const attachInputRef = useRef(null);

  // ── Placeholder picker ──
  const [pickerOpen, setPickerOpen] = useState(false);
  const lastFocused = useRef(null); // {field:'subject'|'block', blockId?, fieldName?, el, selStart, selEnd}
  const subjectRef = useRef(null);

  // ── View tab ──
  const [tab, setTab] = useState('compose'); // 'compose' | 'preview'
  const [previewIdx, setPreviewIdx] = useState(0);

  // ── Send ──
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  // ── Save as template ──
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [savingTemplate, setSavingTemplate] = useState(false);

  // ── Load data ──
  useEffect(() => {
    Promise.all([
      axios.get(`${API}/cohorts`, auth()),
      axios.get(`${API}/applications`, auth()).catch(() => ({ data: [] })),
      axios.get(`${API}/students`, auth()).catch(() => ({ data: [] })),
      axios.get(`${API}/site-config`, auth()).catch(() => ({ data: {} })),
      axios.get(`${API}/email-templates`, auth()).catch(() => ({ data: [] })),
    ]).then(([cohRes, appRes, stuRes, cfgRes, tplRes]) => {
      setCohorts(cohRes.data || []);
      setTemplates((tplRes.data || []).filter(t => t.active));

      const apps = (appRes.data || []).map(a => ({
        email: a.email,
        label: `${a.full_name} <${a.email}>`,
        name: a.full_name,
        type: 'applicant',
      }));
      const stus = (stuRes.data || []).map(s => ({
        email: s.email,
        label: `${s.full_name} <${s.email}>`,
        name: s.full_name,
        type: 'student',
      }));
      // Merge: student record beats applicant record for same email
      const map = new Map();
      apps.forEach(a => map.set(a.email.toLowerCase(), a));
      stus.forEach(s => map.set(s.email.toLowerCase(), s));
      setAllEmails([...map.values()]);

      setLogoUrl(cfgRes.data?.email_logo_url || '');
    }).catch(() => {});
  }, []); // eslint-disable-line

  // ── Typeahead search results ──
  const searchResults = search.length > 0
    ? allEmails
        .filter(e =>
          e.email.toLowerCase().includes(search.toLowerCase()) ||
          (e.name || '').toLowerCase().includes(search.toLowerCase())
        )
        .slice(0, 8)
    : [];

  // ── Close search dropdown on outside click ──
  useEffect(() => {
    const handler = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) setSearchOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ── Cohort auto-populate ──
  useEffect(() => {
    if (mode !== 'cohort' || !selectedCohortId) return;
    axios.get(`${API}/students/cohort/${selectedCohortId}`, auth())
      .then(res => {
        const newChips = (res.data || []).map(s => ({
          email: s.email,
          label: s.full_name ? `${s.full_name} <${s.email}>` : s.email,
          isExternal: false,
        }));
        setChips(newChips);
      })
      .catch(() => toast.error('Failed to load cohort students'));
  }, [selectedCohortId, mode]); // eslint-disable-line

  // ── Chip management ──
  const addChip = (emailEntry) => {
    const email = (emailEntry.email || emailEntry).toLowerCase().trim();
    if (!email || chips.some(c => c.email.toLowerCase() === email)) return;
    const known = allEmails.find(e => e.email.toLowerCase() === email);
    setChips(prev => [...prev, {
      email,
      label: known ? `${known.name} <${email}>` : email,
      isExternal: !known,
    }]);
    setSearch('');
    setSearchOpen(false);
  };

  const removeChip = (email) => setChips(prev => prev.filter(c => c.email !== email));

  const handleSearchKey = (e) => {
    if ((e.key === 'Enter' || e.key === ',') && search.trim()) {
      e.preventDefault();
      const raw = search.trim().replace(/,$/, '');
      if (raw.includes('@')) addChip({ email: raw });
    }
    if (e.key === 'Backspace' && !search && chips.length) {
      setChips(prev => prev.slice(0, -1));
    }
  };

  // ── CSV upload ──
  const handleCSV = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const { columns, rows, error } = parseCSV(ev.target.result);
      if (error) { setCsvError(error); toast.error(error); return; }
      setCsvError('');
      setCsvColumns(columns);
      setChips(rows.map(r => ({
        email: r.email,
        label: r.data?.name ? `${r.data.name} <${r.email}>` : r.email,
        isExternal: false,
        data: r.data,
      })));
      toast.success(`Loaded ${rows.length} recipients from CSV`);
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  // ── Block management ──
  const addBlock = (type) => {
    const base = { id: uid(), type };
    if (type === 'heading') base.level = 2;
    if (type === 'heading' || type === 'paragraph') base.text = '';
    if (type === 'button') { base.label = 'Click Here'; base.url = 'https://'; }
    setBlocks(prev => [...prev, base]);
  };

  const updateBlock = (id, field, value) =>
    setBlocks(prev => prev.map(b => b.id === id ? { ...b, [field]: value } : b));

  const removeBlock = (id) => setBlocks(prev => prev.filter(b => b.id !== id));

  const moveBlock = (index, dir) => {
    const next = index + dir;
    if (next < 0 || next >= blocks.length) return;
    setBlocks(prev => {
      const arr = [...prev];
      [arr[index], arr[next]] = [arr[next], arr[index]];
      return arr;
    });
  };

  const trackFocus = useCallback((info) => { lastFocused.current = info; }, []);

  // ── Placeholder insert ──
  const insertPlaceholder = (key) => {
    const ph = `{{${key}}}`;
    setPickerOpen(false);
    const lf = lastFocused.current;
    if (!lf) return;
    const el = lf.el;
    const start = lf.selStart ?? (el?.value?.length || 0);
    const end = lf.selEnd ?? (el?.value?.length || 0);
    const cur = el?.value || '';
    const newVal = cur.slice(0, start) + ph + cur.slice(end);
    if (lf.field === 'subject') {
      setSubject(newVal);
    } else {
      setBlocks(prev => prev.map(b =>
        b.id === lf.blockId ? { ...b, [lf.fieldName]: newVal } : b
      ));
    }
    requestAnimationFrame(() => {
      if (el) { el.focus(); const pos = start + ph.length; el.setSelectionRange(pos, pos); }
    });
  };

  // ── Attachments ──
  const handleAttachFiles = (e) => {
    Array.from(e.target.files || []).forEach(file => {
      if (file.size > 10 * 1024 * 1024) { toast.error(`${file.name} exceeds 10 MB limit`); return; }
      const reader = new FileReader();
      reader.onload = (ev) => {
        const base64 = ev.target.result.split(',')[1];
        setAttachments(prev => [...prev, {
          filename: file.name,
          fileblob: base64,
          mimetype: file.type || 'application/octet-stream',
          size: file.size,
        }]);
      };
      reader.readAsDataURL(file);
    });
    e.target.value = '';
  };

  // ── Preview data ──
  const previewChip = chips[previewIdx] || chips[0];
  const previewPh = previewChip
    ? {
        applicant_name: previewChip.label?.split('<')[0]?.trim() || '',
        student_name: previewChip.label?.split('<')[0]?.trim() || '',
        email: previewChip.email,
        ...((previewChip.data) || {}),
      }
    : {};

  const previewSubject  = applyPh(subject || selectedTemplate?.subject || '', previewPh);
  const previewFullHtml = selectedTemplate
    ? applyPh(selectedTemplate.body_html, { ...previewPh, logo_url: logoUrl })
    : wrapEmailPreview(applyPh(blocksToHtml(blocks), previewPh), logoUrl);

  // ── Available placeholders ──
  const csvPh = csvColumns.map(c => ({ key: c, label: `CSV: ${c}` }));
  const allPh = mode === 'csv' ? [...STANDARD_PH, ...csvPh] : STANDARD_PH;

  // ── Save as template ──
  const handleSaveTemplate = async () => {
    if (!templateName.trim()) return;
    setSavingTemplate(true);
    try {
      const innerHtml = blocksToHtml(blocks);
      const fullHtml = buildTemplateHtml(innerHtml);
      const placeholders = extractPlaceholders(subject, innerHtml);
      await axios.post(`${API}/email-templates`, {
        template_key: toTemplateKey(templateName),
        name: templateName.trim(),
        subject: subject.trim(),
        body_html: fullHtml,
        placeholders,
        blocks_json: JSON.stringify(blocks),
      }, auth());
      toast.success(`Template "${templateName.trim()}" saved`);
      setSaveDialogOpen(false);
      setTemplateName('');
      // Refresh templates list so new entry appears in the selector immediately
      axios.get(`${API}/email-templates`, auth()).then(r => setTemplates((r.data || []).filter(t => t.active))).catch(() => {});
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : 'Failed to save template');
    } finally {
      setSavingTemplate(false);
    }
  };

  // ── Send ──
  const canSend = chips.length > 0 && (subject.trim() || selectedTemplate?.subject) && (
    selectedTemplate ||
    blocks.some(b => b.type === 'divider' || (b.type === 'button' ? b.label && b.url : b.text?.trim()))
  );

  const handleSend = async () => {
    setSending(true);
    try {
      const recipients = chips.map(c => ({
        email: c.email,
        data: c.data || null,
      }));
      const payload = {
        recipients,
        subject: subject.trim() || selectedTemplate?.subject || '',
        ...(selectedTemplate
          ? { template_key: selectedTemplate.template_key }
          : { body_html: blocksToHtml(blocks) }),
        attachments: attachments.map(({ filename, fileblob, mimetype }) => ({ filename, fileblob, mimetype })),
      };
      const res = await axios.post(`${API}/bulk-email`, payload, auth());
      setResult(res.data);
      toast.success(`Queued ${res.data.queued} emails for delivery`);
      setConfirmOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to send bulk email');
    } finally {
      setSending(false);
    }
  };

  // ─── Render ────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-muted flex flex-col">
      {/* Header */}
      <div className="bg-white border-b px-6 py-4 flex-shrink-0">
        <h1 className="text-xl font-playfair font-bold text-primary">Bulk Email</h1>
        <p className="text-sm text-muted-foreground mt-0.5">Compose and send personalised emails to groups of applicants or students</p>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* ── Left panel: Recipients + Attachments ─────────────── */}
        <div className="w-96 flex-shrink-0 border-r bg-white flex flex-col overflow-y-auto">

          {/* Mode tabs */}
          <div className="px-4 pt-4 pb-3 border-b">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Recipients</p>
            <div className="flex gap-1 p-1 bg-gray-100 rounded-lg">
              {[
                { id: 'individual', label: 'Individual' },
                { id: 'cohort',     label: 'Cohort' },
                { id: 'csv',        label: 'CSV' },
              ].map(m => (
                <button
                  key={m.id}
                  onClick={() => { setMode(m.id); setChips([]); setCsvColumns([]); setCsvError(''); setSelectedCohortId(''); }}
                  className={`flex-1 py-1.5 rounded-md text-xs font-semibold transition-all
                    ${mode === m.id ? 'bg-white text-primary shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                >
                  {m.label}
                </button>
              ))}
            </div>
          </div>

          {/* Chip area */}
          <div className="px-4 py-3 border-b">
            {/* Chips display */}
            <div className="flex flex-wrap gap-1.5 mb-2 min-h-[28px]">
              {chips.map(c => (
                <Chip key={c.email} chip={c} onRemove={removeChip} removable={mode !== 'csv'} />
              ))}
              {chips.length === 0 && (
                <span className="text-xs text-gray-400 italic">No recipients yet</span>
              )}
            </div>

            {/* Mode-specific controls */}
            {mode === 'individual' && (
              <div ref={searchRef} className="relative">
                <input
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                  placeholder="Search name or email, press Enter to add…"
                  value={search}
                  onChange={e => { setSearch(e.target.value); setSearchOpen(true); }}
                  onFocus={() => setSearchOpen(true)}
                  onKeyDown={handleSearchKey}
                />
                {searchOpen && search.length > 0 && (
                  <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-52 overflow-y-auto">
                    {searchResults.length > 0
                      ? searchResults.map(e => (
                          <button
                            key={e.email}
                            className="w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center justify-between text-sm"
                            onMouseDown={(ev) => { ev.preventDefault(); addChip(e); }}
                          >
                            <span>
                              <span className="font-medium">{e.name}</span>
                              <span className="text-gray-400 ml-1 text-xs">{e.email}</span>
                            </span>
                            <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium
                              ${e.type === 'student' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                              {e.type}
                            </span>
                          </button>
                        ))
                      : (
                          <button
                            className="w-full text-left px-3 py-2 hover:bg-gray-50 text-sm text-gray-600 flex items-center gap-2"
                            onMouseDown={(ev) => { ev.preventDefault(); if (search.trim().includes('@')) addChip({ email: search.trim() }); }}
                          >
                            <Plus className="w-3.5 h-3.5 text-gray-400" />
                            Add "{search.trim()}" as external email
                          </button>
                        )
                    }
                  </div>
                )}
              </div>
            )}

            {mode === 'cohort' && (
              <select
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                value={selectedCohortId}
                onChange={e => setSelectedCohortId(e.target.value)}
              >
                <option value="">— Select a cohort —</option>
                {cohorts.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            )}

            {mode === 'csv' && (
              <div>
                <label className="flex items-center justify-center gap-2 border-2 border-dashed border-gray-200 rounded-lg p-3 cursor-pointer hover:border-primary/40 hover:bg-primary/5 transition-colors">
                  <Upload className="w-4 h-4 text-gray-400" />
                  <span className="text-sm text-gray-500">Upload CSV file</span>
                  <input type="file" accept=".csv" className="hidden" onChange={handleCSV} />
                </label>
                {csvError && <p className="text-xs text-red-600 mt-1">{csvError}</p>}
                {csvColumns.length > 0 && (
                  <div className="mt-2 p-2 bg-blue-50 rounded text-xs text-blue-700">
                    <strong>Custom placeholders:</strong> {csvColumns.map(c => `{{${c}}}`).join(', ')}
                  </div>
                )}
                <p className="text-xs text-gray-400 mt-1.5">First row must be headers. Column named <code>email</code> is required.</p>
              </div>
            )}
          </div>

          {/* External email warning */}
          {chips.some(c => c.isExternal) && (
            <div className="mx-4 my-2 p-2 bg-amber-50 border border-amber-200 rounded-lg flex gap-2 text-xs text-amber-700">
              <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5 text-amber-500" />
              <span>{chips.filter(c => c.isExternal).length} external email(s) — standard placeholders like <code>{'{{applicant_name}}'}</code> may not resolve for these.</span>
            </div>
          )}

          {/* Recipient count */}
          {chips.length > 0 && (
            <div className="mx-4 my-2 flex items-center gap-2">
              <Users className="w-4 h-4 text-primary" />
              <span className="text-sm font-semibold text-primary">{chips.length} recipient{chips.length !== 1 ? 's' : ''}</span>
              {mode === 'csv' && csvColumns.length > 0 && (
                <span className="text-xs text-gray-500">· Mail merge</span>
              )}
              <button
                onClick={() => setChips([])}
                className="ml-auto text-xs text-red-500 hover:text-red-700"
              >
                Clear all
              </button>
            </div>
          )}

          <div className="flex-1" />

          {/* Attachments */}
          <div className="px-4 py-3 border-t">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Attachments</p>
              <button
                onClick={() => attachInputRef.current?.click()}
                className="flex items-center gap-1 text-xs text-primary hover:text-primary/80 font-medium"
              >
                <Paperclip className="w-3.5 h-3.5" /> Add file
              </button>
              <input ref={attachInputRef} type="file" multiple accept=".pdf,.doc,.docx,.png,.jpg,.jpeg" className="hidden" onChange={handleAttachFiles} />
            </div>
            {attachments.length === 0
              ? <p className="text-xs text-gray-400 italic">No attachments</p>
              : (
                <div className="space-y-1.5">
                  {attachments.map((a, i) => (
                    <div key={i} className="flex items-center gap-2 p-2 bg-gray-50 rounded text-xs">
                      <Paperclip className="w-3 h-3 text-gray-400 flex-shrink-0" />
                      <span className="flex-1 truncate font-medium">{a.filename}</span>
                      <span className="text-gray-400">{fmt(a.size)}</span>
                      <button onClick={() => setAttachments(prev => prev.filter((_, j) => j !== i))} className="text-red-400 hover:text-red-600">
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )
            }
            <p className="text-[10px] text-gray-400 mt-1.5">Max 10 MB per file · PDF, Word, images</p>
          </div>
        </div>

        {/* ── Right panel: Compose / Preview ───────────────────── */}
        <div className="flex-1 flex flex-col overflow-hidden">

          {/* Tab bar + placeholder picker */}
          <div className="bg-white border-b px-4 py-2 flex items-center justify-between flex-shrink-0">
            <div className="flex gap-1 p-1 bg-gray-100 rounded-lg">
              {[
                { id: 'compose', icon: Pencil, label: 'Compose' },
                { id: 'preview', icon: Eye, label: 'Preview' },
              ].map(t => (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-all
                    ${tab === t.id ? 'bg-white text-primary shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                >
                  <t.icon className="w-3.5 h-3.5" />{t.label}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-2">
              {/* Save as template */}
              <button
                onClick={() => { setTemplateName(''); setSaveDialogOpen(true); }}
                disabled={!!selectedTemplate || (!subject.trim() && !blocks.some(b => b.text?.trim() || b.label?.trim()))}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-medium text-gray-600 hover:border-primary/40 hover:text-primary transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                title="Save current email as a reusable template"
              >
                <BookmarkPlus className="w-3.5 h-3.5" /> Save as Template
              </button>

              {/* Placeholder picker */}
              <div className="relative">
                <button
                  onClick={() => setPickerOpen(o => !o)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-medium transition-colors
                    ${pickerOpen ? 'border-primary bg-primary/5 text-primary' : 'border-gray-200 text-gray-600 hover:border-primary/40'}`}
                  title="Insert placeholder"
                >
                  <Braces className="w-3.5 h-3.5" /> Placeholders
                </button>
                {pickerOpen && (
                  <div className="absolute right-0 top-full mt-1 z-50 bg-white border border-gray-200 rounded-lg shadow-xl w-72 p-2">
                    <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider px-2 mb-1.5">
                      Click to insert at cursor
                    </p>
                    <div className="space-y-0.5">
                      {allPh.map(p => (
                        <button
                          key={p.key}
                          onClick={() => insertPlaceholder(p.key)}
                          className="w-full text-left px-2 py-1.5 rounded hover:bg-primary/5 flex items-center justify-between group"
                        >
                          <span className="text-xs font-mono text-primary">{`{{${p.key}}}`}</span>
                          <span className="text-[10px] text-gray-400 group-hover:text-gray-600">{p.label}</span>
                        </button>
                      ))}
                    </div>
                    {mode !== 'csv' && (
                      <p className="text-[10px] text-gray-400 px-2 pt-2 border-t mt-2">
                        Upload a CSV to unlock custom placeholders.
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Send button */}
              <Button
                size="sm"
                disabled={!canSend}
                onClick={() => setConfirmOpen(true)}
                className="gap-1.5"
              >
                <Send className="w-3.5 h-3.5" />
                Send to {chips.length} recipient{chips.length !== 1 ? 's' : ''}
              </Button>
            </div>
          </div>

          {tab === 'compose' ? (
            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              {/* Template selector */}
              <div>
                <Label className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5 block">Use Saved Template</Label>
                {selectedTemplate ? (
                  <div className="flex items-center gap-2 px-3 py-2 bg-primary/5 border border-primary/20 rounded-lg">
                    <BookmarkPlus className="w-3.5 h-3.5 text-primary flex-shrink-0" />
                    <span className="text-sm font-medium text-primary flex-1 truncate">{selectedTemplate.name}</span>
                    <button
                      onClick={() => { setSelectedTemplate(null); setSubject(''); }}
                      className="text-xs text-gray-400 hover:text-red-500 flex items-center gap-1"
                    >
                      <X className="w-3 h-3" /> Clear
                    </button>
                  </div>
                ) : (
                  <select
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 text-gray-600"
                    value=""
                    onChange={e => {
                      const tpl = templates.find(t => t.template_key === e.target.value);
                      if (!tpl) return;
                      if (tpl.blocks_json) {
                        // Template was composed with the block editor — restore blocks
                        try { setBlocks(JSON.parse(tpl.blocks_json)); } catch { /* malformed */ }
                        setSubject(tpl.subject || '');
                        setSelectedTemplate(null); // block editor mode
                      } else {
                        // System/seeded template — use full HTML as-is
                        setSelectedTemplate(tpl);
                        setSubject(tpl.subject || '');
                      }
                    }}
                  >
                    <option value="">— Or pick a saved template —</option>
                    {templates.map(t => (
                      <option key={t.template_key} value={t.template_key}>{t.name}</option>
                    ))}
                  </select>
                )}
              </div>

              {/* Subject */}
              <div>
                <Label className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5 block">Subject</Label>
                <Input
                  ref={subjectRef}
                  placeholder="Email subject… use {{applicant_name}} for personalisation"
                  value={subject}
                  onChange={e => setSubject(e.target.value)}
                  onFocus={e => lastFocused.current = { field: 'subject', el: e.target, selStart: e.target.selectionStart, selEnd: e.target.selectionEnd }}
                  onMouseUp={e => lastFocused.current = { field: 'subject', el: e.target, selStart: e.target.selectionStart, selEnd: e.target.selectionEnd }}
                  onKeyUp={e => lastFocused.current = { field: 'subject', el: e.target, selStart: e.target.selectionStart, selEnd: e.target.selectionEnd }}
                  className="text-sm"
                />
              </div>

              {selectedTemplate ? (
                /* Template mode — show inline preview of the template */
                <div className="space-y-2">
                  <p className="text-xs text-gray-400">
                    This template cannot be edited here. To modify it, go to Settings → Email Templates.
                    The preview below is a static rendering — switch to the <strong>Preview</strong> tab to see it with recipient placeholders applied.
                  </p>
                  <iframe
                    title="Template preview"
                    srcDoc={applyPh(selectedTemplate.body_html, { logo_url: logoUrl })}
                    className="w-full rounded-lg border border-gray-200 shadow-sm bg-white"
                    style={{ height: '520px', border: 'none' }}
                    sandbox="allow-same-origin"
                  />
                </div>
              ) : (
                <>
                  {/* Block list */}
                  <div className="space-y-3">
                    {blocks.map((block, i) => (
                      <BlockEditor
                        key={block.id}
                        block={block}
                        index={i}
                        total={blocks.length}
                        onUpdate={updateBlock}
                        onRemove={removeBlock}
                        onMove={moveBlock}
                        onTrackFocus={(info) => {
                          lastFocused.current = {
                            ...info,
                            selStart: info.el?.selectionStart,
                            selEnd: info.el?.selectionEnd,
                          };
                        }}
                      />
                    ))}
                  </div>

                  {/* Add block buttons */}
                  <div className="flex flex-wrap gap-2 pt-1">
                    {[
                      { type: 'heading',   icon: Type,         label: '+ Heading' },
                      { type: 'paragraph', icon: AlignLeft,    label: '+ Paragraph' },
                      { type: 'button',    icon: MousePointer, label: '+ Button' },
                      { type: 'divider',   icon: Minus,        label: '+ Divider' },
                    ].map(({ type, icon: Icon, label }) => (
                      <button
                        key={type}
                        onClick={() => addBlock(type)}
                        className="flex items-center gap-1.5 px-3 py-1.5 border border-dashed border-gray-300 rounded-lg text-xs text-gray-500 hover:border-primary hover:text-primary hover:bg-primary/5 transition-colors"
                      >
                        <Icon className="w-3.5 h-3.5" />{label}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          ) : (
            /* ── Preview tab ── */
            <div className="flex-1 overflow-hidden flex flex-col">
              {/* Preview recipient selector (CSV mode) */}
              {chips.length > 1 && (
                <div className="bg-white border-b px-4 py-2 flex items-center gap-3 flex-shrink-0">
                  <span className="text-xs text-gray-500">Preview as:</span>
                  <select
                    className="border border-gray-200 rounded px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-primary/30 max-w-[280px]"
                    value={previewIdx}
                    onChange={e => setPreviewIdx(Number(e.target.value))}
                  >
                    {chips.map((c, i) => (
                      <option key={c.email} value={i}>{c.label || c.email}</option>
                    ))}
                  </select>
                  <span className="text-xs text-gray-400">Subject: <em>{previewSubject || '(no subject)'}</em></span>
                </div>
              )}
              <div className="flex-1 overflow-auto bg-gray-100 p-4">
                <iframe
                  title="Email preview"
                  srcDoc={previewFullHtml}
                  className="w-full max-w-2xl mx-auto block rounded-lg shadow-md bg-white"
                  style={{ height: 'calc(100vh - 220px)', border: 'none' }}
                  sandbox="allow-same-origin"
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Result banner */}
      {result && (
        <div className="fixed bottom-4 right-4 z-50 bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3 shadow-lg max-w-sm">
          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
          <div>
            <p className="text-green-800 text-sm font-medium">Emails queued successfully</p>
            <p className="text-green-600 text-xs">{result.queued} of {result.total} queued for delivery</p>
          </div>
          <button onClick={() => setResult(null)} className="ml-auto text-green-600 hover:text-green-800">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Save as template dialog */}
      <Dialog open={saveDialogOpen} onOpenChange={setSaveDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Save as Template</DialogTitle>
            <DialogDescription>
              This email will be saved to Settings → Email Templates and can be reused for individual or bulk sends.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <div>
              <Label className="text-xs font-semibold text-gray-600 mb-1.5 block">Template Name</Label>
              <Input
                autoFocus
                placeholder="e.g. Cohort Enrolment Welcome"
                value={templateName}
                onChange={e => setTemplateName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSaveTemplate()}
              />
            </div>
            <p className="text-xs text-gray-400">Subject: <em>{subject || '(no subject)'}</em></p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveTemplate} disabled={!templateName.trim() || savingTemplate}>
              {savingTemplate ? 'Saving…' : 'Save Template'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm dialog */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Confirm Bulk Send</DialogTitle>
            <DialogDescription asChild>
              <div className="space-y-2 text-sm text-gray-600">
                <p>
                  This will send <strong>{chips.length}</strong> email{chips.length !== 1 ? 's' : ''}.
                  {attachments.length > 0 && ` Includes ${attachments.length} attachment${attachments.length !== 1 ? 's' : ''}.`}
                </p>
                {chips.some(c => c.isExternal) && (
                  <div className="flex gap-2 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-700">
                    <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                    {chips.filter(c => c.isExternal).length} external recipient(s) may have unresolved placeholders.
                  </div>
                )}
                <p className="text-xs text-gray-400">Subject: <em>{subject}</em></p>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>Cancel</Button>
            <Button onClick={handleSend} disabled={sending}>
              {sending ? 'Sending…' : `Send ${chips.length} email${chips.length !== 1 ? 's' : ''}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
